// AnalyticsDashboard.js
// Comprehensive analytics dashboard for FlowTutor owner
// Shows user behavior, feature usage, engagement metrics, and trends

import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import './AnalyticsDashboard.css';
import {
  BarChart3, Users, Clock, Target, TrendingUp, Activity,
  Calendar, Zap, Award, Eye, MousePointer, X, RefreshCw,
  Download, Filter, Search, BookOpen, Code, FileText
} from 'lucide-react';

const AnalyticsDashboard = ({ onClose, userEmail, darkMode }) => {
  // State for analytics data
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7'); // days
  const [refreshing, setRefreshing] = useState(false);

  // Metrics state
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalEvents, setTotalEvents] = useState(0);
  const [activeSessions, setActiveSessions] = useState(0);
  const [avgSessionDuration, setAvgSessionDuration] = useState(0);

  // Feature usage data
  const [featureUsage, setFeatureUsage] = useState([]);
  const [topLearningTopics, setTopLearningTopics] = useState([]);
  const [dailyActiveUsers, setDailyActiveUsers] = useState([]);
  const [hourlyPatterns, setHourlyPatterns] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);

  // Practice test analytics
  const [practiceTestStats, setPracticeTestStats] = useState([]);
  const [codeInterpreterStats, setCodeInterpreterStats] = useState({});

  // Check if user is admin
  const isAdmin = userEmail === 'pranayrishi.nalem@gmail.com';

  useEffect(() => {
    if (isAdmin) {
      loadAnalytics();
    }
  }, [dateRange, isAdmin]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadOverviewMetrics(),
        loadFeatureUsage(),
        loadLearningTopics(),
        loadDailyActiveUsers(),
        loadHourlyPatterns(),
        loadRecentEvents(),
        loadPracticeTestStats(),
        loadCodeInterpreterStats()
      ]);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOverviewMetrics = async () => {
    const daysAgo = new Date();
    daysAgo.setDate(daysAgo.getDate() - parseInt(dateRange));

    // Total unique users
    const { data: users, error: usersError } = await supabase
      .from('analytics_events')
      .select('user_id')
      .gte('created_at', daysAgo.toISOString());

    if (!usersError && users) {
      const uniqueUsers = new Set(users.map(u => u.user_id)).size;
      setTotalUsers(uniqueUsers);
    }

    // Total events
    const { count } = await supabase
      .from('analytics_events')
      .select('*', { count: 'exact', head: true })
      .gte('created_at', daysAgo.toISOString());

    setTotalEvents(count || 0);

    // Active sessions
    const { data: sessions } = await supabase
      .from('user_sessions')
      .select('duration_seconds')
      .gte('started_at', daysAgo.toISOString());

    if (sessions) {
      setActiveSessions(sessions.length);
      const avgDuration = sessions.reduce((sum, s) => sum + (s.duration_seconds || 0), 0) / sessions.length;
      setAvgSessionDuration(Math.round(avgDuration));
    }
  };

  const loadFeatureUsage = async () => {
    const daysAgo = new Date();
    daysAgo.setDate(daysAgo.getDate() - parseInt(dateRange));

    const { data, error } = await supabase
      .from('analytics_events')
      .select('event_type, user_id')
      .eq('event_category', 'feature_usage')
      .gte('created_at', daysAgo.toISOString());

    if (!error && data) {
      // Aggregate by feature
      const featureMap = {};
      data.forEach(event => {
        if (!featureMap[event.event_type]) {
          featureMap[event.event_type] = { count: 0, users: new Set() };
        }
        featureMap[event.event_type].count++;
        featureMap[event.event_type].users.add(event.user_id);
      });

      const features = Object.entries(featureMap).map(([name, data]) => ({
        name,
        count: data.count,
        uniqueUsers: data.users.size
      })).sort((a, b) => b.count - a.count);

      setFeatureUsage(features);
    }
  };

  const loadLearningTopics = async () => {
    const daysAgo = new Date();
    daysAgo.setDate(daysAgo.getDate() - parseInt(dateRange));

    const { data, error } = await supabase
      .from('analytics_events')
      .select('event_data, user_id')
      .eq('event_type', 'roadmap_generated')
      .gte('created_at', daysAgo.toISOString());

    if (!error && data) {
      const topicMap = {};
      data.forEach(event => {
        const topic = event.event_data?.topic;
        if (topic) {
          if (!topicMap[topic]) {
            topicMap[topic] = { count: 0, users: new Set() };
          }
          topicMap[topic].count++;
          topicMap[topic].users.add(event.user_id);
        }
      });

      const topics = Object.entries(topicMap).map(([topic, data]) => ({
        topic,
        count: data.count,
        uniqueUsers: data.users.size
      })).sort((a, b) => b.count - a.count).slice(0, 20);

      setTopLearningTopics(topics);
    }
  };

  const loadDailyActiveUsers = async () => {
    const daysAgo = new Date();
    daysAgo.setDate(daysAgo.getDate() - parseInt(dateRange));

    const { data, error } = await supabase
      .from('analytics_events')
      .select('created_at, user_id')
      .gte('created_at', daysAgo.toISOString())
      .order('created_at', { ascending: true });

    if (!error && data) {
      const dailyMap = {};
      data.forEach(event => {
        const date = new Date(event.created_at).toISOString().split('T')[0];
        if (!dailyMap[date]) {
          dailyMap[date] = new Set();
        }
        dailyMap[date].add(event.user_id);
      });

      const daily = Object.entries(dailyMap).map(([date, users]) => ({
        date,
        users: users.size
      }));

      setDailyActiveUsers(daily);
    }
  };

  const loadHourlyPatterns = async () => {
    const daysAgo = new Date();
    daysAgo.setDate(daysAgo.getDate() - parseInt(dateRange));

    const { data, error } = await supabase
      .from('analytics_events')
      .select('created_at')
      .gte('created_at', daysAgo.toISOString());

    if (!error && data) {
      const hourlyMap = {};
      for (let i = 0; i < 24; i++) {
        hourlyMap[i] = 0;
      }

      data.forEach(event => {
        const hour = new Date(event.created_at).getHours();
        hourlyMap[hour]++;
      });

      const hourly = Object.entries(hourlyMap).map(([hour, count]) => ({
        hour: parseInt(hour),
        count
      }));

      setHourlyPatterns(hourly);
    }
  };

  const loadRecentEvents = async () => {
    const { data, error } = await supabase
      .from('analytics_events')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);

    if (!error && data) {
      setRecentEvents(data);
    }
  };

  const loadPracticeTestStats = async () => {
    const daysAgo = new Date();
    daysAgo.setDate(daysAgo.getDate() - parseInt(dateRange));

    const { data, error } = await supabase
      .from('analytics_events')
      .select('event_type, event_data')
      .eq('event_category', 'assessment')
      .gte('created_at', daysAgo.toISOString());

    if (!error && data) {
      const testMap = {};
      data.forEach(event => {
        const testType = event.event_data?.test_type || event.event_type;
        if (!testMap[testType]) {
          testMap[testType] = { count: 0, avgScore: 0, scores: [] };
        }
        testMap[testType].count++;
        if (event.event_data?.score) {
          testMap[testType].scores.push(event.event_data.score);
        }
      });

      const tests = Object.entries(testMap).map(([type, data]) => ({
        type,
        count: data.count,
        avgScore: data.scores.length > 0
          ? Math.round(data.scores.reduce((a, b) => a + b, 0) / data.scores.length)
          : 0
      }));

      setPracticeTestStats(tests);
    }
  };

  const loadCodeInterpreterStats = async () => {
    const daysAgo = new Date();
    daysAgo.setDate(daysAgo.getDate() - parseInt(dateRange));

    const { data, error } = await supabase
      .from('analytics_events')
      .select('event_data')
      .like('event_type', '%code%')
      .gte('created_at', daysAgo.toISOString());

    if (!error && data) {
      const languages = {};
      let totalChallenges = 0;
      let totalRuns = 0;

      data.forEach(event => {
        if (event.event_data?.language) {
          languages[event.event_data.language] = (languages[event.event_data.language] || 0) + 1;
        }
        if (event.event_data?.action === 'generate_challenge') totalChallenges++;
        if (event.event_data?.action === 'run_code') totalRuns++;
      });

      setCodeInterpreterStats({
        languages,
        totalChallenges,
        totalRuns
      });
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await loadAnalytics();
    setRefreshing(false);
  };

  const exportData = () => {
    const data = {
      overview: { totalUsers, totalEvents, activeSessions, avgSessionDuration },
      featureUsage,
      topLearningTopics,
      dailyActiveUsers,
      practiceTestStats,
      codeInterpreterStats,
      exportedAt: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flowtutor-analytics-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isAdmin) {
    return (
      <div className="analytics-unauthorized">
        <h2>Access Denied</h2>
        <p>You don't have permission to view the analytics dashboard.</p>
        <button onClick={onClose} className="btn-primary">Close</button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="spinner-large"></div>
        <p>Loading analytics data...</p>
      </div>
    );
  }

  return (
    <div className={`analytics-dashboard ${darkMode ? 'dark' : ''}`}>
      {/* Header */}
      <div className="analytics-header">
        <div className="header-left">
          <h1>📊 FlowTutor Analytics Dashboard</h1>
          <p className="header-subtitle">Owner Dashboard - Real-time insights and metrics</p>
        </div>
        <div className="header-actions">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="date-range-select"
          >
            <option value="1">Last 24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
          <button onClick={refreshData} className="btn-icon" disabled={refreshing}>
            <RefreshCw className={refreshing ? 'spinning' : ''} size={20} />
          </button>
          <button onClick={exportData} className="btn-icon" title="Export Data">
            <Download size={20} />
          </button>
          <button onClick={onClose} className="btn-close">
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Overview Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon users">
            <Users size={28} />
          </div>
          <div className="metric-content">
            <h3>Total Users</h3>
            <p className="metric-value">{totalUsers.toLocaleString()}</p>
            <span className="metric-label">Unique users in period</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon events">
            <Activity size={28} />
          </div>
          <div className="metric-content">
            <h3>Total Events</h3>
            <p className="metric-value">{totalEvents.toLocaleString()}</p>
            <span className="metric-label">User interactions tracked</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon sessions">
            <Zap size={28} />
          </div>
          <div className="metric-content">
            <h3>Active Sessions</h3>
            <p className="metric-value">{activeSessions.toLocaleString()}</p>
            <span className="metric-label">User sessions</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon duration">
            <Clock size={28} />
          </div>
          <div className="metric-content">
            <h3>Avg Session</h3>
            <p className="metric-value">{Math.floor(avgSessionDuration / 60)}m {avgSessionDuration % 60}s</p>
            <span className="metric-label">Average duration</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-content">
        {/* Feature Usage */}
        <div className="dashboard-card feature-usage-card">
          <h2><Target size={20} /> Feature Usage</h2>
          <p className="card-subtitle">Most used features and their engagement</p>
          <div className="feature-usage-list">
            {featureUsage.slice(0, 10).map((feature, idx) => (
              <div key={idx} className="feature-item">
                <div className="feature-info">
                  <span className="feature-name">{feature.name}</span>
                  <span className="feature-users">{feature.uniqueUsers} users</span>
                </div>
                <div className="feature-bar-container">
                  <div
                    className="feature-bar"
                    style={{ width: `${(feature.count / featureUsage[0]?.count) * 100}%` }}
                  ></div>
                  <span className="feature-count">{feature.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Learning Topics */}
        <div className="dashboard-card topics-card">
          <h2><BookOpen size={20} /> Top Learning Topics</h2>
          <p className="card-subtitle">Most searched learning goals</p>
          <div className="topics-list">
            {topLearningTopics.slice(0, 15).map((topic, idx) => (
              <div key={idx} className="topic-item">
                <span className="topic-rank">#{idx + 1}</span>
                <span className="topic-name">{topic.topic}</span>
                <span className="topic-count">{topic.count} searches</span>
              </div>
            ))}
          </div>
        </div>

        {/* Daily Active Users Chart */}
        <div className="dashboard-card daily-users-card">
          <h2><TrendingUp size={20} /> Daily Active Users</h2>
          <p className="card-subtitle">User activity over time</p>
          <div className="chart-container">
            {dailyActiveUsers.map((day, idx) => (
              <div key={idx} className="chart-bar-wrapper">
                <div
                  className="chart-bar"
                  style={{ height: `${(day.users / Math.max(...dailyActiveUsers.map(d => d.users))) * 100}%` }}
                  title={`${day.date}: ${day.users} users`}
                ></div>
                <span className="chart-label">{new Date(day.date).getDate()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Hourly Usage Pattern */}
        <div className="dashboard-card hourly-pattern-card">
          <h2><Calendar size={20} /> Hourly Usage Pattern</h2>
          <p className="card-subtitle">Peak activity hours</p>
          <div className="hourly-grid">
            {hourlyPatterns.map((hour, idx) => {
              const maxCount = Math.max(...hourlyPatterns.map(h => h.count));
              const intensity = maxCount > 0 ? (hour.count / maxCount) * 100 : 0;
              return (
                <div
                  key={idx}
                  className="hourly-cell"
                  style={{ opacity: 0.2 + (intensity / 100) * 0.8 }}
                  title={`${hour.hour}:00 - ${hour.count} events`}
                >
                  <span className="hour-label">{hour.hour}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Practice Test Stats */}
        <div className="dashboard-card test-stats-card">
          <h2><Award size={20} /> Practice Test Analytics</h2>
          <p className="card-subtitle">Test completion and performance</p>
          <div className="test-stats-list">
            {practiceTestStats.map((test, idx) => (
              <div key={idx} className="test-stat-item">
                <span className="test-type">{test.type}</span>
                <div className="test-metrics">
                  <span className="test-metric">
                    <FileText size={14} /> {test.count} attempts
                  </span>
                  <span className="test-metric">
                    <Target size={14} /> {test.avgScore}% avg
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Code Interpreter Stats */}
        <div className="dashboard-card code-stats-card">
          <h2><Code size={20} /> Code Interpreter Usage</h2>
          <p className="card-subtitle">Programming language preferences</p>
          <div className="code-stats">
            <div className="code-summary">
              <div className="code-metric">
                <span className="code-metric-label">Challenges Generated</span>
                <span className="code-metric-value">{codeInterpreterStats.totalChallenges || 0}</span>
              </div>
              <div className="code-metric">
                <span className="code-metric-label">Code Executions</span>
                <span className="code-metric-value">{codeInterpreterStats.totalRuns || 0}</span>
              </div>
            </div>
            <div className="language-list">
              {Object.entries(codeInterpreterStats.languages || {}).map(([lang, count], idx) => (
                <div key={idx} className="language-item">
                  <span className="language-name">{lang}</span>
                  <span className="language-count">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Events */}
        <div className="dashboard-card recent-events-card full-width">
          <h2><Eye size={20} /> Recent User Activity</h2>
          <p className="card-subtitle">Latest events and interactions</p>
          <div className="events-table-wrapper">
            <table className="events-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>User</th>
                  <th>Event Type</th>
                  <th>Category</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {recentEvents.slice(0, 20).map((event, idx) => (
                  <tr key={idx}>
                    <td className="time-cell">
                      {new Date(event.created_at).toLocaleTimeString()}
                    </td>
                    <td className="user-cell">
                      {event.user_email || event.user_id.substring(0, 8)}
                    </td>
                    <td className="event-cell">{event.event_type}</td>
                    <td className="category-cell">
                      <span className={`category-badge ${event.event_category}`}>
                        {event.event_category}
                      </span>
                    </td>
                    <td className="details-cell">
                      {event.event_data && Object.keys(event.event_data).length > 0
                        ? JSON.stringify(event.event_data).substring(0, 50) + '...'
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
